// MergeConflict - Python Developer
// Histoire : Un sprint catastrophique — conflits de code partout.

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface MergeConflictScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const mergeConflictCharacters: Character[] = [
  { id: 'priya',  name: 'Priya',  role: 'Tech Lead',           color: '#065F46', avatar: 'priya'  },
  { id: 'marcus', name: 'Marcus', role: 'Backend Engineer',     color: '#1E3A5F', avatar: 'marcus' },
  { id: 'viktor', name: 'Viktor', role: 'Frontend Architect',   color: '#4338CA', avatar: 'viktor' },
  { id: 'olga',   name: 'Olga',   role: 'Engineering Manager',  color: '#7C2D12', avatar: 'olga'   },
];

// ── Chapitre I : La Collision ─────────────────────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'mc-scene-1',
  title: 'Scene 1 : Code review qui degenere',
  duration: 4,
  content: {
    narrativeText:
      "Lundi matin. Pull Request ouverte depuis 3 jours. 47 commentaires. Personne n'a merge.\n\n" +
      "**Marcus :** \"Viktor, ton refactor du module auth casse mon API de paiement. 12 fichiers en conflit.\"\n\n" +
      "**Viktor :** \"Mon refactor est propre. C'est ton code legacy qui n'est pas conforme aux nouvelles conventions.\"\n\n" +
      "**Marcus :** \"'Legacy' ? Ca marche depuis 2 ans sans incident !\"\n\n" +
      "**Priya :** \"Stop. On ne discute pas de qui a tort. On discute de comment resoudre les 12 conflits d'ici vendredi.\"\n\n" +
      "**Olga :** \"Et comment on evite ca la prochaine fois ? Ca fait 3 sprints qu'on a des conflits massifs.\"\n\n" +
      "**Priya :** \"Le probleme c'est qu'on travaille sur des branches longues. Marcus, ta branche a 3 mois. Viktor, la tienne a 6 semaines.\"\n\n" +
      "**Viktor :** \"Et alors ? On ne peut pas merger des features incompletes.\"\n\n" +
      "**Priya :** \"C'est la le probleme. En trunk-based development, on merge tous les jours avec des feature flags. Pas de branches longues.\"\n\n" +
      "**Marcus :** \"Ca implique refactorer toute notre strategie de branching.\"\n\n" +
      "**Olga :** \"C'est exactement ce qu'on va faire. Mais d'abord, on resout les conflits actuels. Priya, tu facilites ?\"\n\n" +
      "**Priya :** \"On a 5 jours. On commence maintenant.\"",
    characters: ['priya', 'marcus', 'viktor', 'olga'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'mc-card-1',
  title: 'Concept : Strategies de branching',
  duration: 5,
  content: {
    title: 'Git Branching Strategies : GitFlow vs Trunk-Based vs Feature Flags',
    body:
      "## Strategies de branching Git\n\n" +
      "### 1. GitFlow\n" +
      "```\nmain\n  └── develop\n       ├── feature/xxx  (2-4 semaines)\n       ├── release/1.0\n       └── hotfix/yyy\n```\n\n" +
      "**Avantages** : Structure claire, releases previsibles\n" +
      "**Inconvenients** : Branches longues = merge conflicts massifs. Overhead de gestion elevee.\n\n" +
      "### 2. Trunk-Based Development\n" +
      "```\nmain (trunk) — tout le monde merge ici chaque jour\n  ├── short-lived branches (< 2 jours)\n  └── feature flags pour les features incompletes\n```\n\n" +
      "**Avantages** : Pas de conflits massifs, integration continue reelle\n" +
      "**Inconvenients** : Necessite discipline + feature flags + bonne couverture de tests\n\n" +
      "### 3. GitHub Flow\n" +
      "```\nmain\n  └── feature/xxx (courtes, 1-3 jours)\n       └── Pull Request → review → merge\n```\n\n" +
      "**Avantages** : Simple, adapte aux releases continues\n" +
      "**Inconvenients** : Pas de concept de release formelle\n\n" +
      "### Comparaison\n" +
      "| Critere | GitFlow | Trunk-Based | GitHub Flow |\n" +
      "|---|---|---|---|\n" +
      "| Conflits | Eleves | Faibles | Moyens |\n" +
      "| Complexite | Elevee | Moyenne | Faible |\n" +
      "| CI/CD | Difficile | Naturel | Simple |\n" +
      "| Adapte si | Releases planifiees | Deploiement continu | SaaS/web |",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'mc-chat-1',
  title: 'Discussion : Rebase vs Merge',
  duration: 6,
  content: {
    tutorCharacterId: 'priya',
    messages: [
      {
        id: 'mc-chat-1-q1',
        sender: 'character',
        characterId: 'priya',
        text: "Marcus veut git merge, Viktor prefere git rebase. Quelle est la difference fondamentale ?",
        type: 'single-choice',
        options: [
          "Merge = historique lineaire, Rebase = historique avec les commits de merge",
          "Merge = conserve l'historique original avec un commit de merge, Rebase = rejoue les commits sur une nouvelle base (historique lineaire)",
          "Il n'y a pas de difference pratique",
          "Rebase est toujours preferable pour les equipes",
        ],
        correctAnswer: "Merge = conserve l'historique original avec un commit de merge, Rebase = rejoue les commits sur une nouvelle base (historique lineaire)",
        explanation: "git merge cree un commit de merge qui preserve l'historique divergent. git rebase reapplique les commits de votre branche sur le sommet de la branche cible, creant un historique lineaire plus lisible mais reecrivant l'histoire.",
      },
      {
        id: 'mc-chat-1-q2',
        sender: 'character',
        characterId: 'priya',
        text: "Quelle est la regle d'or concernant git rebase ?",
        type: 'single-choice',
        options: [
          "Ne jamais rebaser une branche partagee avec d'autres developpeurs",
          "Toujours rebaser avant de merger",
          "Rebaser uniquement les branches de feature",
          "Rebaser uniquement les commits de la semaine",
        ],
        correctAnswer: "Ne jamais rebaser une branche partagee avec d'autres developpeurs",
        explanation: "git rebase reecrit l'historique. Si d'autres developpeurs ont base leur travail sur vos commits, le rebase cree des divergences irreconciliables. Rebase uniquement les branches locales non poussees, ou avec --force-with-lease si necessaire.",
      },
      {
        id: 'mc-chat-1-q3',
        sender: 'character',
        characterId: 'priya',
        text: "Comment resoudre proprement un merge conflict dans un fichier Python ?",
        type: 'multiple-choice',
        options: [
          "Comprendre les deux versions avec git diff avant de choisir",
          "Utiliser git checkout --ours pour prendre sa version systematiquement",
          "Editer manuellement le fichier pour garder la logique correcte des deux versions",
          "Apres resolution : git add <fichier> puis git commit",
        ],
        correctAnswer: [
          "Comprendre les deux versions avec git diff avant de choisir",
          "Editer manuellement le fichier pour garder la logique correcte des deux versions",
          "Apres resolution : git add <fichier> puis git commit",
        ],
        explanation: "La resolution correcte necessite de comprendre les deux versions, d'editer le fichier manuellement (pas de git checkout --ours qui ignore l'autre version), et de marquer le conflit resolu avec git add avant le commit.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'mc-task-1',
  title: 'Resoudre un merge conflict complexe Python',
  duration: 30,
  content: {
    platform: 'Terminal Git / VS Code',
    objective: "Resoudre les 12 merge conflicts entre les branches feature/auth-refactor et feature/payment-api",
    steps: [
      {
        stepNumber: 1,
        action: "Diagnostiquer les conflits",
        detail: "git log --merge --oneline  # Voir les commits en conflit\ngit diff --name-only HEAD...MERGE_HEAD  # Lister les fichiers conflictuels",
      },
      {
        stepNumber: 2,
        action: "Ouvrir le premier fichier en conflit",
        detail: "Identifiez les marqueurs: <<<<<<< HEAD (votre version), ======= (separateur), >>>>>>> feature/auth-refactor (leur version). Lisez les deux versions pour comprendre l'intention.",
      },
      {
        stepNumber: 3,
        action: "Resoudre les conflits de contenu Python",
        detail: "Pour les imports dupliques : garder les imports unifies. Pour les fonctions reecrites : combiner la logique si possible. Pour les constantes : prendre la version la plus recente.",
      },
      {
        stepNumber: 4,
        action: "Marquer les conflits resolus",
        detail: "Apres chaque fichier resolu : git add <fichier>. Verifier : git status (aucun 'both modified' restant).",
      },
      {
        stepNumber: 5,
        action: "Finaliser et tester",
        detail: "git commit -m 'resolve: merge conflicts auth-refactor and payment-api'\npython -m pytest tests/  # Verifier que les tests passent",
      },
    ],
    validation: "git status propre. Tous les tests unitaires passent. PR approuvee par Priya.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'mc-quiz-1',
  title: 'Quiz : Git et Python Developer',
  duration: 5,
  content: {
    questions: [
      {
        id: 'mc-q1-1',
        question: "Quelle commande Git montre les commits qui ont cree un merge conflict ?",
        type: 'single-choice',
        options: [
          "git log --conflict",
          "git log --merge",
          "git diff --conflict",
          "git status --verbose",
        ],
        correctAnswer: "git log --merge",
        explanation: "git log --merge affiche les commits des deux branches qui ont modifie les fichiers en conflit. Combine avec --oneline pour une lecture rapide de l'historique du conflit.",
      },
      {
        id: 'mc-q1-2',
        question: "Dans Python, quel outil permet d'imposer des conventions de code pour eviter les conflits de style ?",
        type: 'single-choice',
        options: [
          "pylint uniquement",
          "black + flake8 + isort",
          "mypy uniquement",
          "pytest",
        ],
        correctAnswer: "black + flake8 + isort",
        explanation: "black (formatage automatique) + flake8 (linting PEP8) + isort (tri des imports) constituent le trio standard pour imposer un style uniforme. Configures en pre-commit hook, ils eliminant les conflits de style entre developpeurs.",
      },
      {
        id: 'mc-q1-3',
        question: "Qu'est-ce qu'un feature flag et pourquoi est-il utile dans le trunk-based development ?",
        type: 'single-choice',
        options: [
          "Un flag dans le code qui marque les features a supprimer",
          "Un mecanisme pour activer/desactiver une feature en production sans nouveau deploiement",
          "Une balise Git pour les releases de features",
          "Un type de branche Git speciaux",
        ],
        correctAnswer: "Un mecanisme pour activer/desactiver une feature en production sans nouveau deploiement",
        explanation: "Les feature flags permettent de merger du code incomplet dans main (trunk) en le cachant derriere un flag desactive. La feature est activee quand elle est prete, sans nouvelle branche. Essentiel pour le trunk-based development.",
      },
    ],
  } as QuizContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'La Collision',
  subtitle: 'Python Developer — Git, conflits et collaboration',
  learningObjectives: [
    "Comprendre et resoudre les merge conflicts Git",
    "Choisir entre GitFlow, Trunk-Based et GitHub Flow",
    "Maitriser git log, git diff et les outils de resolution",
    "Comprendre la difference entre merge et rebase",
    "Configurer des pre-commit hooks pour eviter les conflits de style",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1],
};

export const mergeConflictScenario: MergeConflictScenario = {
  id: 'merge-conflict',
  title: 'The Merge Conflict',
  subtitle: 'Un sprint qui tourne au cauchemar technique',
  certification: 'Python Developer',
  coverImage: require('../../../assets/images/characters/nina_authority.png'),
  characters: mergeConflictCharacters,
  chapters: [chapter1],
};

export default mergeConflictScenario;
